// src/pages/Cart/Cart.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCart } from '../../hooks/useCart.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs.jsx';
import { StickerCartLine } from '../../components/cart/StickerCartLine/StickerCartLine.jsx';
import { getStickerCart } from '../../store/stickerCartSlice.js';

export const Cart = () => {
  const { items, total, count, getCart, updateCartItem, removeFromCart, clearCart } = useCart();
  const { success, error } = useToast();
  const dispatch = useDispatch();

  // ============================================================
  // 🔴 СТИКЕРЫ В КОРЗИНЕ (п.87, 2026-10-29)
  //    До этой правки страница знала только про товары и показывала
  //    «Корзина пуста», даже когда стикер уже лежал в корзине.
  //    Стикеры живут в ОТДЕЛЬНОМ срезе (`stickerCart`), потому что это
  //    другой тип позиции — разбор в `store/stickerCartSlice.js`.
  //    Суммы складываем здесь, а не на сервере: у двух корзин разные
  //    валютные источники и разные правила доставки, а «одна сумма на
  //    всё» — задача оформления заказа, где сервер считает итог.
  // ============================================================
  const sticker = useSelector((st) => st.stickerCart);
  useEffect(() => {
    dispatch(getStickerCart());
  }, [dispatch]);

  const hasStickers = sticker.items.length > 0;
  const grandTotal = Number(total || 0) + Number(sticker.total || 0);
  const totalItems = Number(count || 0) + Number(sticker.count || 0);
  const isEmpty = items.length === 0 && !hasStickers && sticker.isLoaded;

  // 🔴 КОРЗИНА НЕ ЗАГРУЖАЛАСЬ ПРИ ОТКРЫТИИ СТРАНИЦЫ.
  //    Замер: товар добавлялся (POST 200, GET 200, items: 1), но на
  //    /cart НЕ УХОДИЛО НИ ОДНОГО запроса — посетитель видел «Корзина
  //    пуста», хотя товар лежал. Корзина наполнялась только как
  //    побочный эффект addToCart; при прямой загрузке (F5, ссылка,
  //    заход с телефона) стор пуст и заполнить его было некому.
  //    ⚠️ Симптом коварен: без перезагрузки всё выглядит рабочим.
  useEffect(() => {
    getCart();
  }, [getCart]);

  const handleQuantityChange = (id, quantity) => {
    if (quantity < 1) return;
    updateCartItem(id, { quantity });
  };

  const handleRemove = async (id) => {
    try {
      await removeFromCart(id);
      success('Товар удален из корзины');
    } catch (err) {
      error('Не удалось удалить товар');
    }
  };

  const handleClearCart = async () => {
    if (window.confirm('Вы уверены, что хотите очистить корзину?')) {
      try {
        await clearCart();
        success('Корзина очищена');
      } catch (err) {
        error('Не удалось очистить корзину');
      }
    }
  };

  const breadcrumbs = [{ label: 'Корзина', path: '/cart' }];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: 'rgb(var(--c-text))',
          marginTop: '24px',
          marginBottom: '32px',
        }}>
          Корзина
        </h1>

        {isEmpty ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 0',
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '20px',
            border: '1px solid rgb(var(--c-border))',
          }}>
            <p style={{ fontSize: '18px', color: 'rgb(var(--c-text-secondary))' }}>Корзина пуста</p>
            <Link to="/catalog">
              <Button style={{ marginTop: '16px' }}>Перейти в каталог</Button>
            </Link>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '32px',
          }}>
            {/* Список товаров */}
            <div>
              {/* СТИКЕРЫ — отдельным блоком выше товаров: они новые и
                  их чаще всего и оформляют. */}
              {hasStickers && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgb(var(--c-text-secondary))', marginBottom: 10 }}>
                    Наклейки
                  </div>
                  {sticker.items.map((line) => (
                    <StickerCartLine key={line.id} item={line} />
                  ))}
                </div>
              )}
              {items.length > 0 && hasStickers && (
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgb(var(--c-text-secondary))', marginBottom: 10 }}>
                  Товары
                </div>
              )}
              {items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    gap: '20px',
                    padding: '20px',
                    backgroundColor: 'rgb(var(--c-surface))',
                    border: '1px solid rgb(var(--c-border))',
                    borderRadius: '16px',
                    marginBottom: '12px',
                    alignItems: 'center',
                  }}
                >
                  <div style={{
                    width: '100px',
                    height: '100px',
                    backgroundColor: 'rgb(var(--c-surface-2))',
                    borderRadius: '8px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgb(var(--c-text-secondary))',
                    fontSize: '12px',
                  }}>
                    Фото
                  </div>

                  <div style={{ flex: 1 }}>
                    <Link
                      to={`/product/${item.product_id}`}
                      style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: 'rgb(var(--c-text))',
                        textDecoration: 'none',
                      }}
                    >
                      {item.Product?.name || 'Товар'}
                    </Link>
                    {item.calculator_data && Object.keys(item.calculator_data).length > 0 && (
                      <div style={{
                        fontSize: '12px',
                        color: 'rgb(var(--c-text-secondary))',
                        marginTop: '4px',
                      }}>
                        {Object.entries(item.calculator_data).map(([key, value]) => (
                          <span key={key} style={{ marginRight: '12px' }}>
                            {key}: {value}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: 'rgb(var(--c-accent))',
                      marginTop: '8px',
                    }}>
                      {item.price_at_time} ₽
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '1px solid rgb(var(--c-border))',
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                    >
                      −
                    </button>
                    <span style={{ minWidth: '32px', textAlign: 'center', fontWeight: 500 }}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '1px solid rgb(var(--c-border))',
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                    >
                      +
                    </button>
                  </div>

                  <button
                    onClick={() => handleRemove(item.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgb(var(--c-text-secondary))',
                      cursor: 'pointer',
                      fontSize: '20px',
                      padding: '8px',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Итог */}
            <div>
              <div style={{
                backgroundColor: 'rgb(var(--c-surface))',
                border: '1px solid rgb(var(--c-border))',
                borderRadius: '16px',
                padding: '24px',
                position: 'sticky',
                top: '100px',
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  Итог заказа
                </h3>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid rgb(var(--c-border))',
                }}>
                  <span style={{ color: 'rgb(var(--c-text-secondary))' }}>Позиций ({totalItems} шт.)</span>
                  <span style={{ fontWeight: 500 }}>{grandTotal.toLocaleString('ru-RU')} ₽</span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: '1px solid rgb(var(--c-border))',
                }}>
                  <span style={{ color: 'rgb(var(--c-text-secondary))' }}>Доставка</span>
                  <span style={{ fontWeight: 500 }}>Рассчитывается</span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '16px 0',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'rgb(var(--c-text))',
                }}>
                  <span>Итого</span>
                  <span>{grandTotal.toLocaleString('ru-RU')} ₽</span>
                </div>

                <Link to="/checkout" style={{ textDecoration: 'none', display: 'block' }}>
                  <Button fullWidth style={{ marginTop: '12px' }}>
                    Оформить заказ
                  </Button>
                </Link>

                <button
                  onClick={handleClearCart}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgb(var(--c-danger))',
                    fontSize: '14px',
                    cursor: 'pointer',
                    marginTop: '12px',
                    width: '100%',
                  }}
                >
                  Очистить корзину
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};